import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Container } from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import CodeIcon from '@mui/icons-material/Code';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

const solutionSnippets = [
  {
    title: "Smart Business Intelligence",
    code: `// AI-enhanced analytics for client dashboard
const insights = await analyzeBusinessMetrics({
  sales: salesData,
  customers: customerBehavior,
  market: marketTrends
});

return {
  recommendations: insights.strategic_advice,
  predictions: insights.future_trends,
  confidence: 0.94
};`
  },
  {
    title: "Intelligent Customer Support",
    code: `// AI-powered support system
const response = await processCustomerQuery({
  query: userMessage,
  context: customerHistory,
  knowledgeBase: companyDocs
});

return {
  answer: response.solution,
  escalate: response.needsHuman,
  satisfaction: response.confidence
};`
  },
  {
    title: "Predictive Operations",
    code: `// AI for operational efficiency
const optimization = await predictiveAnalysis({
  inventory: currentStock,
  demand: historicalData,
  seasonality: marketPatterns
});

return {
  reorderPoints: optimization.inventory_levels,
  costSavings: optimization.projected_savings,
  riskFactors: optimization.alerts
};`
  }
];

export const HeroSection = () => {
  const [currentSnippet, setCurrentSnippet] = useState(0);
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsTyping(true);
      setTimeout(() => {
        setCurrentSnippet((prev) => (prev + 1) % solutionSnippets.length);
        setIsTyping(false);
      }, 800);
    }, 8000);

    return () => clearInterval(interval);
  }, []);

  return (
    <Container maxWidth="xl" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', py: 4 }}>
      <Box sx={{ width: '100%', display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 8, alignItems: 'center' }}>
        
        {/* Left side - Content */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Box sx={{ mb: 3 }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              <Typography 
                level="h1" 
                sx={{ 
                  fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                  mb: 2,
                  lineHeight: 1.1
                }}
              >
                We Build Solutions That{' '}
                <Box component="span" sx={{ 
                  background: 'linear-gradient(135deg, #00A86B 0%, #2E5BBA 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>
                  Think Ahead
                </Box>
              </Typography>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
            >
              <Typography 
                level="h3" 
                sx={{ 
                  color: 'text.secondary', 
                  mb: 4,
                  fontSize: { xs: '1.2rem', md: '1.5rem' },
                  fontWeight: 400
                }}
              >
                Technology consultants who strategically integrate AI to solve real business problems.
                <br />We don't just build software – we craft intelligent solutions.
              </Typography>
            </motion.div>
          </Box>

          {/* Features */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.6 }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
              {[
                { icon: <AutoAwesomeIcon />, text: "Strategic AI integration that drives business value" },
                { icon: <CodeIcon />, text: "Full-stack solutions enhanced with intelligent features" },
                { icon: <AutoAwesomeIcon />, text: "Future-proof technology that adapts and learns" }
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8 + index * 0.1, duration: 0.5 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ 
                      color: '#00A86B', 
                      display: 'flex',
                      '& svg': { fontSize: '1.2rem' }
                    }}>
                      {feature.icon}
                    </Box>
                    <Typography level="body1" sx={{ color: 'text.secondary' }}>
                      {feature.text}
                    </Typography>
                  </Box>
                </motion.div>
              ))}
            </Box>
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.6 }}
          >
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Link to="/contact" style={{ textDecoration: 'none' }}>
                <Button 
                  variant="solid" 
                  color="primary" 
                  size="lg"
                  sx={{ px: 4, py: 1.5 }}
                  startDecorator={<AutoAwesomeIcon />}
                >
                  Get Project Estimate
                </Button>
              </Link>
              <Button 
                variant="outlined" 
                color="primary" 
                size="lg"
                sx={{ px: 4, py: 1.5 }}
                onClick={() => {
                  const element = document.getElementById('assessment');
                  element?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                See AI Analysis Demo
              </Button>
            </Box>
          </motion.div>
        </motion.div>

        {/* Right side - Live Code Demo */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <Box sx={{ 
            background: 'rgba(0, 0, 0, 0.9)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(100, 116, 139, 0.5)',
            borderRadius: 2,
            p: 3,
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Terminal header */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1, 
              mb: 3,
              borderBottom: '1px solid rgba(100, 116, 139, 0.2)',
              pb: 2
            }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', background: '#ff5f56' }} />
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', background: '#ffbd2e' }} />
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', background: '#27ca3f' }} />
              </Box>
              <Typography level="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                AI Solution Examples
              </Typography>
            </Box>

            {/* Code content */}
            <Box sx={{ minHeight: 200 }}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentSnippet}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                >
                  <Typography 
                    level="body2" 
                    sx={{ 
                      color: '#60a5fa', 
                      mb: 2,
                      fontFamily: 'monospace',
                      fontWeight: 600
                    }}
                  >
                    {solutionSnippets[currentSnippet].title}
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      fontFamily: 'Monaco, "Cascadia Code", "Roboto Mono", monospace',
                      fontSize: '0.85rem',
                      color: '#f1f5f9',
                      lineHeight: 1.5,
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      '& .comment': { color: '#cbd5e1' },
                      '& .keyword': { color: '#60a5fa' },
                      '& .string': { color: '#34d399' },
                    }}
                  >
                    {solutionSnippets[currentSnippet].code}
                  </Box>
                </motion.div>
              </AnimatePresence>

              {/* Typing indicator */}
              {isTyping && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  style={{
                    position: 'absolute',
                    bottom: 20,
                    right: 20,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    background: 'rgba(96, 165, 250, 0.15)',
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: '1px solid rgba(96, 165, 250, 0.4)'
                  }}
                >
                  <AutoAwesomeIcon sx={{ fontSize: '1rem', color: '#60a5fa' }} />
                  <Typography level="body2" sx={{ color: '#60a5fa' }}>
                    AI consulting...
                  </Typography>
                </motion.div>
              )}
            </Box>
          </Box>
        </motion.div>
      </Box>
    </Container>
  );
};